'use client';

import Image from 'next/image';
import styles from './ContestSection.module.scss';
import Text from '@components/atoms/Text';
import Button from '@components/atoms/Button';
import { contestSectionData } from './ContestSection.data';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useMemo } from 'react';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { getLoginUrl } from '@utils/getLoginUrl';
import { useWishlist } from '@context/WishlistContext';
import WishlistGIF from '@components/atoms/WishlistGIF';
import { formatCompleteDate, formatDateShort, getMonthFromDate } from '@helpers/dateFormatter';
import { getContestUI, isParticipatedAndEntriesClosed } from '@helpers/contestLogic';
import { Modal } from '@components/atoms/Modal';
import { PAGE_TYPES } from '@constants/pageTypes';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import assetLoader from '@utils/internalImageLoder';
import { usePersistor } from '@lib/redux/PersistorContext';
import { useAppDispatch } from '@lib/redux/hooks';
import { clearProfile } from '@lib/redux/slices/profileSlice';
import { closeWebView } from '@hooks/useWebviewCallback';
import { useIsMobile } from '@hooks/use-mobile';
import { contestStates, postCampaignOptIn, postIntentCaptured } from '@services/carDropService';
import VideoModalWrapper from '@components/molecules/VideoModalWrapper';
import { postLead } from '@services/leadCaptureService';
import { getCityFromIp } from '@utils/cityDetection';

const ContestSection = ({
  contestState,
  isLoggedIn,
  campaignData,
  upcomingCampaignData,
  cityName,
  campaignOptInData,
  userId,
  campaign_stage,
  car_policyholder,
  isUserParticipated,
  completedCampaign,
  allCompletedCampaigns,
  userProfile,
  isIntentCaptured
}: any) => {

  const { wishlistItems } = useWishlist();
  const campaignWishlistCount = wishlistItems.length;

  const dispatch = useAppDispatch();
  const router = useRouter();
  const persistor = usePersistor();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [failureModal, setFailureModal] = useState<boolean>(false);
  const [showParticipatedModal, setShowParticipatedModal] = useState<boolean>(false);
  const [showPlanningModal, setShowPlanningModal] = useState<boolean>(false);
  const [selectedPlanningOption, setSelectedPlanningOption] = useState<string>('');
  const [selectedCampaignIndex, setSelectedCampaignIndex] = useState<number>(0);
  const isMobile = useIsMobile();

  const sortedCompletedCampaigns = useMemo(() => {
    if (!allCompletedCampaigns || allCompletedCampaigns.length === 0) return [];
    return [...allCompletedCampaigns].sort((a: any, b: any) => {
      const dateA = new Date(a?.startDate || 0).getTime();
      const dateB = new Date(b?.startDate || 0).getTime();
      return dateB - dateA;
    });
  }, [allCompletedCampaigns]);

  const planningOptions = [
    { id: 'next_3_months', label: 'Within the next 3 months' },
    { id: 'next_6_months', label: 'Within the next 6 months' },
    { id: 'not_any_time_soon', label: 'Not any time soon' }
  ];

  const selectedCampaign = useMemo(() => {
    return sortedCompletedCampaigns?.[selectedCampaignIndex] || completedCampaign;
  }, [sortedCompletedCampaigns, selectedCampaignIndex, completedCampaign]);

  const selectedWinnerName = selectedCampaign?.metadata?.winnerName || '';
  const selectedWinnerCity = selectedCampaign?.metadata?.winnerCity || '';
  const selectedCashbackWinnerData = selectedCampaign?.metadata?.cashBackWinners || [];

  const isSelectedCampaignPreviousMonth = useMemo(() => {
    if (!selectedCampaign?.startDate) return false;
    
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const campaignDate = new Date(selectedCampaign.startDate);
    
    return (
      campaignDate.getMonth() === previousMonth.getMonth() &&
      campaignDate.getFullYear() === previousMonth.getFullYear()
    );
  }, [selectedCampaign?.startDate]);

  const videoUrl = useMemo(() => {
    const monthName = getMonthFromDate(selectedCampaign?.startDate);
    if(monthName == "November"){
      return "https://ackodrive-prod.ackoassets.com/video/generic/nov_car_drop_winner_vertical.m3u8"
    }else{
      return `https://ackodrive-prod.ackoassets.com/video/generic/dec_car_drop_winner_vertical.m3u8`;
    }
  }, [selectedCampaign?.startDate]);

  const desktopVideoUrl = useMemo(() => {
    const monthName = getMonthFromDate(selectedCampaign?.startDate);
    if(monthName == "December"){
      return "https://ackodrive-prod.ackoassets.com/video/generic/dec_car_drop_winner_horizontal.m3u8";
    }
    return undefined;
  }, [selectedCampaign?.startDate]);

  const metaData = useMemo(() => {
    const monthName = getMonthFromDate(selectedCampaign?.startDate);
    if(monthName == "November"){
      return {
        authorName: "ACKO DRIVE",
        authorType: "ACKO DRIVE",
        duration: "127",
        mediaSource: "S3",
        primaryText: "ACKO Drive Car Drop Winner",
        secondaryText: "",
        thumbnail: `${assetLoader()}/images/winnerThumbnail.png`,
        title: "ACKO Drive Car Drop Winner",
      }
    }else{
      return {
        authorName: "ACKO DRIVE",
        authorType: "ACKO DRIVE",
        duration: "127",
        mediaSource: "S3",
        primaryText: "ACKO Drive Car Drop Winner",
        secondaryText: "",
        thumbnail: `${assetLoader()}/images/CarDrop/winnerWithKey.png`,
        title: "ACKO Drive Car Drop Winner",
      };
    }
  }, [selectedCampaign?.startDate]);

  let hasParticipated = (contestState === "ALREADY_PARTICIPATED" || contestState === "NEW_PARTICIPANT" || isUserParticipated) && campaignOptInData;

  const contestMonth = useMemo(() => {
    return getMonthFromDate(campaignData?.metadata?.productRevealDate);
  }, [campaignData?.metadata?.productRevealDate]);

  const winnerState = useMemo(() => {
    if (!campaignData?.metadata?.productRevealDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const revealDate = new Date(campaignData.metadata.productRevealDate);
    revealDate.setHours(0, 0, 0, 0);

    return revealDate.getTime() <= today.getTime();
  }, [campaignData?.metadata?.productRevealDate]);

  const uiConfig = useMemo(() => {
    return getContestUI(campaignData, upcomingCampaignData, contestState, campaignWishlistCount, isLoggedIn, campaignOptInData, isUserParticipated);
  }, [campaignData, upcomingCampaignData, contestState, campaignWishlistCount, isLoggedIn, campaignOptInData, isUserParticipated]);

  const isContestClosedWithNoUpcoming = useMemo(() => {
    return !upcomingCampaignData && (contestState === "NO_ACTIVE_CAMPAIGN" || contestState === "COMPLETED");
  }, [upcomingCampaignData, contestState]);

  const isAlreadyParticipatedAndEntriesClosed = useMemo(() => {
    return isParticipatedAndEntriesClosed(contestState, campaignData?.endDate, isUserParticipated);
  }, [contestState, campaignData?.endDate, isUserParticipated]);

  useEffect(() => {
    if (campaignData?.status === 'ACTIVE' && (contestState === "NO_ACTIVE_POLICY" || contestState === "POLICY_EXPIRED")) {
      setFailureModal(true);
    }
  }, [contestState])

  useEffect(() => {
    if (failureModal) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [failureModal]);

  useEffect(()=>{
    if(showPlanningModal){
      (async()=>{
        await trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
          pageType: PAGE_TYPES.CAR_DROP_PAGE,
          additionalProps: {
              logged_in: isLoggedIn ? 'Yes' : 'No',
              phone: userProfile?.phone || userProfile?.contact_no,
              campaign_name: `${getMonthFromDate(campaignData?.startDate)} Car Drop`,
              view_type: 'view_page',
              view_name:'view_car_drop_intent',
              city_name: cityName,
          },
        });
      })();
    }
  },[showPlanningModal]);

  const getButtonText = () => {
    if (uiConfig.buttonDisabled) {
      const dateToShow = uiConfig.useUpcomingDates
        ? upcomingCampaignData?.startDate
        : campaignData?.metadata?.winnerRevealDate;
      return dateToShow ? `${uiConfig.buttonText}${formatDateShort(dateToShow)}` : uiConfig.buttonText;
    }
    return uiConfig.buttonText;
  };

  const handleEnterContest = async () => {
    if (uiConfig.buttonDisabled) {
      return;
    }

    if (isLoggedIn) {
      showLoader();
      if (!campaignOptInData) {
        await postCampaignOptIn(campaignData?.id, userId);
        const states = await contestStates(userId);
        hasParticipated = states === "ALREADY_PARTICIPATED";
      }
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_wishlist_cta",
          city_name: cityName,
          campaign_stage: campaign_stage,
          car_policyholder: car_policyholder,
          campaign_name: "car_drop"
        },
      });

      if (hasParticipated && isIntentCaptured) {
        hideLoader();
        setShowParticipatedModal(true);
        return;
      } else if(hasParticipated && !isIntentCaptured) {
        hideLoader();
        setShowPlanningModal(true);
        return;
      }

      router.push("/campaign-cars/?hide_app_bar=true");
    } else {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_check_eligibility",
          city_name: cityName,
          campaign_name: "car_drop"
        },
      });
      window.location.href = getLoginUrl(window.location.href);
    }
  };

  const handleFailureContest = async () => {
    showLoader();
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_explore_ad",
        city_name: cityName,
        campaign_stage: "Not eligible",
        car_policyholder: "No",
        campaign_name: "car_drop"
      },
    });
    router.push("/")
  };

  const handleTryAgain = async () => {
    try {
      showLoader();

      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_try_again",
          city_name: cityName,
          campaign_stage: "Not eligible",
          car_policyholder: "No",
          campaign_name: "car_drop"
        },
      });

      const basePath = process.env.NEXT_PUBLIC_BASE_TERM
        ? `${process.env.NEXT_PUBLIC_BASE_TERM}`
        : "";

      const logoutUrl = `/${basePath}api/auth/logout?redirect=${encodeURIComponent(
        window.location.pathname
      )}`;

      const response = await fetch(logoutUrl);
      const result = await response.json();

      if (!result.success) {
        console.error("Logout API error:", result.error);
      }

      dispatch(clearProfile());
      await persistor.flush();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      window.location.href = getLoginUrl(window.location.href);
    }
  }
  const refreshPage = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "participation_success",
        city_name: cityName,
        campaign_name: "car_drop"
      },
    });
    window.location.reload();
  }

  const handleTermsLink = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_car_drop_tnc",
        city_name: cityName,
        campaign_stage: campaign_stage,
        car_policyholder: car_policyholder,
        campaign_name: "car_drop"
      },
    });
  }

  const handlePlanningContinue = async () => {
    if (!selectedPlanningOption) return;

    if (selectedPlanningOption === 'next_3_months' || selectedPlanningOption === 'next_6_months') {
      try {
        const ipCity = await getCityFromIp();
        await postLead({
          phone: userProfile?.phone || userProfile?.contact_no,
          city: cityName,
          ip_city: ipCity,
          buying_period: selectedPlanningOption === 'next_3_months' ? 'Within the next 3 months' : 'Within the next 6 months',
          mx_source: 'car_drop_campaign'
        });
      } catch (error) {
        console.error('Lead capture error:', error);
      }
    }

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
          logged_in: isLoggedIn ? 'Yes' : 'No',
          phone: userProfile?.phone || userProfile?.contact_no,
          campaign_name: `${getMonthFromDate(campaignData?.startDate)} Car Drop`,
          action_type: 'tap_btn',
          action_name:'tap_btn_car_drop_intent',
          city_name: cityName,
          intent: selectedPlanningOption === 'next_3_months' ? 'Within the next 3 months' : selectedPlanningOption === 'next_6_months' ? 'Within the next 6 months' : 'Not any time soon',
      },
    });

    await postIntentCaptured(campaignData?.id, userId);
    setShowPlanningModal(false);
    setSelectedPlanningOption('');
    setShowParticipatedModal(true);
  };

  const handleWinnerClick = () => {
    showLoader();
    router.push('/car-drop/winner/declaration/?hide_app_bar=true');
  }

  return (
    <div className={styles.contestSectionContainer}>

      <div className={styles.HeadingContentDesktop}>
        {
          uiConfig.title &&
          <Text className={uiConfig.title.includes("Closed") ? styles.RedTitle : styles.Title}>{uiConfig.title}</Text>
        }
        <Text className={styles.SubTitle}>
          {isContestClosedWithNoUpcoming
            ? "You just missed this one"
            : uiConfig.useUpcomingDates && upcomingCampaignData?.startDate
              ? `${uiConfig.subtitle}`
              : uiConfig.subtitle
          }
        </Text>
        {uiConfig.additionalSubTitle && (
          <Text className={styles.additionalSubTitle}>
            {uiConfig.additionalSubTitle}
          </Text>
        )}
      </div>

      {
        uiConfig.showSteps && (
          <div className={styles.stepsContainer}>
            {contestSectionData.NeedToDo.steps.map((step, index) => (
              <div key={index} className={styles.stepItem}>
                <div className={styles.stepIconWrapper}>
                  {
                    step.icon.includes("contestWishlist") ?
                      (
                        <WishlistGIF
                          width={28}
                          height={28}
                        />
                      ) : (
                        <Image
                          src={`${assetLoader()}${step.icon}`}
                          alt={step.alt}
                          width={28}
                          height={28}
                        />
                      )
                  }
                </div>
                <Text className={styles.stepLabel}>
                  {step.label}
                </Text>
              </div>
            ))}
          </div>
        )
      }

      {uiConfig.showDateReveal && (
        <div className={styles.contestRevealContainer}>
          <Image
            src={`${assetLoader()}/images/ContestPattern.png`}
            alt="Contest Pattern"
            fill
            loading="lazy"
            className={styles.ContestPatternImage}
          />

          {!isAlreadyParticipatedAndEntriesClosed && (
            <div className={uiConfig.showFullDateDetails ? styles.contestRevealDates : styles.entriesClosedRevealDates}>
              {uiConfig.showFullDateDetails ? (
                <>
                  <Text className={styles.contestRevealHeading}>The Car Drop reveal happens on</Text>
                  <Text className={styles.contestRevealDate}>
                    {formatDateShort(campaignData.metadata.productRevealDate)}
                  </Text>
                  <div className={styles.ContestDataContainer}>
                    <div className={styles.dateRevealCard}>
                      <div className={styles.dateRevealContent}>
                        <div className={styles.dateRevealItem}>
                          <Text className={styles.dateRevealLabel}>
                            Entries open
                          </Text>
                          <Text className={styles.dateRevealDate}>
                            {formatDateShort(campaignData.startDate)}
                          </Text>
                        </div>

                        <Image
                          src={`${assetLoader()}/icons/contestDateDivider.svg`}
                          alt="Divider"
                          fill
                          className={styles.dateDivideIcon}
                        />

                        <div className={styles.dateRevealItem}>
                          <Text className={styles.dateRevealLabel}>
                            Entries close
                          </Text>
                          <Text className={styles.dateRevealDate}>
                            {formatDateShort(campaignData.endDate)}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.entriesClosedRevealDatesContianer}>
                  <Text className={styles.entriesCloseDate}>
                    Entries closes on {formatDateShort(campaignData.endDate)}
                  </Text>
                </div>
              )
              }
            </div>
          )}

          <div className={uiConfig.showFullDateDetails ? styles.WinningAnnouncementContainer : styles.entriesCloseWinningAnnouncementContainer}>
            <Image
              src={`${assetLoader()}/icons/CarDropRevealDate.svg`}
              alt="CarDropRevealDate"
              width={64}
              height={64}
            />
            <div className={styles.WinningAnnouncementContent}>
              <Text className={styles.WinningAnnouncementHeading}>Winner announcement</Text>
              <Text className={styles.WinningAnnouncementDate}>
                {formatCompleteDate(campaignData?.metadata?.winnerRevealDate)}
              </Text>
            </div>
          </div>
        </div>
      )}

      {
        completedCampaign && completedCampaign?.metadata?.winnerName && completedCampaign?.metadata?.productRevealDate &&
        <div className={styles.winnerSection}>
          <Text className={styles.winnerSectionDescMobile}>Meet our Car Drop Winners</Text>
          <Text className={styles.winnerSectionDescDesktop}>Meet our Car Drop Winners</Text>

          {
            sortedCompletedCampaigns && sortedCompletedCampaigns.length > 0 && (
              <div className={styles.winnerSectionButtonsContainer}>
                {
                  sortedCompletedCampaigns.map((campaign: any, index: number) => (
                    <Button
                      className={`${styles.winnerSectionButtons} ${selectedCampaignIndex === index ? styles.winnerSectionButtonsActive : ''}`}
                      onClick={() => setSelectedCampaignIndex(index)}
                      key={index}
                    >
                      {getMonthFromDate(campaign?.startDate)}
                    </Button>
                  ))
                }
              </div>
            )
          }
          <div className={styles.winnerDetailsSection}>
            <VideoModalWrapper cityName={cityName} metaData={metaData} videoUrl={videoUrl} desktopVideoUrl={desktopVideoUrl}>
              <Image
                src={`${assetLoader()}/images/CarDrop/carDropWinnerPerson${getMonthFromDate(selectedCampaign?.startDate)}.webp`}
                alt="About AD Car Drop"
                fill
                loading='lazy'
                className={styles.winnerSectionImage}
              />
            </VideoModalWrapper>
            <div className={styles.winnerDetailsInnerSection}>
              <div className={styles.winnerDetails}>
                <Text className={styles.winnerName}>{selectedWinnerName}</Text>
                <Text className={styles.winnerLocation}>{selectedWinnerCity}</Text>
              </div>

              {isSelectedCampaignPreviousMonth && (!selectedCashbackWinnerData || selectedCashbackWinnerData.length === 0) && (
                <>
                  <hr className={styles.winnerSectionMiddleLine} />
                  <div className={styles.winnerSectionInnerContent}>
                    <Text className={styles.winnerSectionInnerContentHead}>
                      Additional reward exclusively for {getMonthFromDate(selectedCampaign?.startDate)} participants
                    </Text>
                    <Image
                      src={`${assetLoader()}/images/CarDrop/CarDropCashback_${isMobile ? 'Mobile':'Desktop'}.webp`}
                      alt="About AD Car Drop"
                      fill
                      loading='lazy'
                      className={styles.winnerSectionInnerContentImage}
                    />
                    <Text className={styles.winnerSectionInnerContentSubText}>
                      Valid for Express Delivery cars booked on ACKO Drive between 1st–31st Jan 2026
                    </Text>
                  </div>
                  <Button className={styles.winnerButton} onClick={handleWinnerClick}>
                    See details
                  </Button>
                </>
              )}

              {selectedCashbackWinnerData && selectedCashbackWinnerData.length > 0 && (
                <>
                  <hr className={styles.winnerSectionMiddleLine} />
                  <div className={styles.winnerCashbackSection}>
                    <Text className={styles.winnerCashbackSectionText}>Lucky buyers who won <span>₹2 lakh cashback</span></Text>
                    <div className={styles.winnerCashbackSectionDetails}>
                      {
                        selectedCashbackWinnerData.map((winner: any, index: number) => (
                          <div key={index} className={styles.winnerCashbackSectionUserDetails}>
                            <Text className={styles.winnerCashbackSectionUserName}>{winner.winnerName}</Text>
                            <Text className={styles.winnerCashbackSectionUserCarAndCity}>{winner.carName} • {winner.winnerCity}</Text>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  {isSelectedCampaignPreviousMonth && (
                    <Button className={styles.winnerButton} onClick={handleWinnerClick}>
                      See details
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      }


      <div className={styles.stepsDescriptionContainer}>
        <Text className={styles.stepsDescriptionHeading}>Drop everything else!<br />ACKO Drive Car Drop is back.</Text>
        <Image
          src={`${assetLoader()}/images/${isMobile ? "AboutADCarDropNew" : "AboutADCarDropDesktopNew"}.svg`}
          alt="About AD Car Drop"
          fill
          loading='lazy'
          className={styles.AboutADCarDrop}
        />
      </div>

      {!isContestClosedWithNoUpcoming && uiConfig.showButton && (
        <div className={styles.modalContainer}>
          <Button
            className={`${styles.contestButton} ${uiConfig.buttonDisabled ? styles.notifyButton : ''}`}
            onClick={handleEnterContest}
          >
            {getButtonText()}
          </Button>
          {uiConfig.showDisclaimer && (
            <div className={styles.disclaimerContainer}>
              <Text className={styles.disclaimerText}>
                By participating, you agree to our {" "}
                <a
                  href="/car-drop/terms-and-conditions/?hide_app_bar=true"
                  className={styles.disclaimerLink}
                  onClick={handleTermsLink}
                >
                  Terms and conditions
                </a>
              </Text>
            </div>
          )}
        </div>
      )}

      {!isIntentCaptured && showPlanningModal && (
        <Modal
          isOpen={showPlanningModal}
          onClose={() => {}}
          overlayClassName={styles.planningModalOverlay}
          contentClassName={styles.planningModalContent}
          headerClassName={styles.planningModalHeaderClassName}
          showCloseButton={false}
        >
          <div className={styles.planningModalContainer}>
            <Text className={styles.planningModalHeading}>
              When are you planning to buy a new car?
            </Text>
            <div className={styles.planningOptionsContainer}>
              {planningOptions.map((option) => (
                <button
                  key={option.id}
                  className={`${styles.planningOption} ${selectedPlanningOption === option.id ? styles.planningOptionSelected : ''}`}
                  onClick={() => setSelectedPlanningOption(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button 
              className={`${styles.planningContinueButton} ${!selectedPlanningOption ? styles.planningContinueButtonDisabled : ''}`}
              onClick={handlePlanningContinue}
              disabled={!selectedPlanningOption}
            >
              Continue
            </Button>
          </div>
        </Modal>
      )}

      {showParticipatedModal && (
        <Modal
          isOpen={showParticipatedModal}
          onClose={() => refreshPage()}
          overlayClassName={styles.finalModalOverlay}
          contentClassName={styles.finalModalContent}
          headerClassName={styles.finalModalHeaderClassName}
          showCloseButton={false}
        >
          <div className={styles.CampaignFinalModalContainer}>
            <Image
              src={`${assetLoader()}/icons/Close_cross.svg`}
              alt="Close_cross"
              width={24}
              height={24}
              className={styles.CampaignFinalModalT1Close}
              onClick={() => refreshPage()}
            />
            <Image
              src={`${assetLoader()}/images/CarDropLogo.webp`}
              alt="CarDropLogo"
              width={116}
              height={24}
              loading="eager"
              className={styles.CarDropLogoFinalModal}
            />
            <div className={styles.CampaignFinalModalT1Container}>
              <Text className={styles.CampaignFinalModalT1}>5 cars wishlisted!</Text>
              <Image
                src={`${assetLoader()}/icons/campaignStart.svg`}
                alt="campaignStart"
                width={24}
                height={24}
                className={styles.campaignStartIcon}
              />
            </div>
            <Text className={styles.CampaignFinalModalT2}>
              You&apos;re officially part of our {contestMonth} Car Drop
            </Text>
            {
              winnerState ?
                <Text className={styles.CampaignFinalModalT3}>
                  Winner announcement on <span className={styles.CampaignFinalModalSpan}>
                    {formatDateShort(campaignData.metadata.winnerRevealDate)}
                  </span>
                </Text>
                :
                <Text className={styles.CampaignFinalModalT3}>
                  The Car Drop reveal happens on <span className={styles.CampaignFinalModalSpan}>
                    {formatDateShort(campaignData.metadata.productRevealDate)}
                  </span>
                </Text>
            }
            <ul className={styles.CampaignFinalModalT4Container}>
              <li className={styles.CampaignFinalModalT4List}>Your wishlist qualifies you for the Car Drop.</li>
              <li className={styles.CampaignFinalModalT4List}>Giveaway car(s) may vary from your wishlist.</li>
              <li className={styles.CampaignFinalModalT4List}>ACKO Drive covers the full on-road cost — registration and road tax included. Any additional taxes on the prize value are payable by the winner.</li>
            </ul>
            <Button className={styles.CampaignFinalModalButton} onClick={() => refreshPage()}>
              Got it
            </Button>
          </div>
        </Modal>
      )}

      {
        failureModal && (
          <div className={styles.failureModalContianer}>
            <div className={styles.failureModalHeader}>
              <Image
                src={`${assetLoader()}/icons/AckoDriveLogo.svg`}
                alt="AckoDriveLogo"
                width={72}
                height={24}
              />
              <Image
                src={`${assetLoader()}/icons/Close_cross.svg`}
                alt="AckoDriveLogo"
                width={24}
                height={24}
                onClick={() => closeWebView(() => router.back())}
              />
            </div>

            {
              contestState === "NO_ACTIVE_POLICY" && (
                <>
                  <div className={styles.failureModalContentContainer}>
                    <div className={styles.failureContent}>
                      <Image
                        src={`${assetLoader()}/icons/NO_ACTIVE_POLICY.svg`}
                        alt="NO_ACTIVE_POLICY"
                        width={88}
                        height={88}
                      />
                      <Text className={styles.failureContentHeading}>
                        We checked under the hood.<br />No policy found.
                      </Text>
                      <Text className={styles.failureContentSubHead}>
                        Car Drop is only for ACKO car policyholders.
                      </Text>
                      <div className={styles.dividedLine}></div>
                      <Text className={styles.differentNumber}>Think you used a different number? <span onClick={handleTryAgain} >Try again</span></Text>
                    </div>
                    <Text className={styles.failureTroubleEmail}>If you’re still facing some trouble, write to us at <br /><span>cardrop@ackodrive.com</span></Text>
                  </div>
                </>
              )
            }

            {
              contestState === "POLICY_EXPIRED" && (
                <div className={styles.failureModalContentContainer}>
                  <div className={styles.failureContentExpied}>
                    <Image
                      src={`${assetLoader()}/icons/POLICY_EXPIRED.svg`}
                      alt="POLICY_EXPIRED"
                      width={88}
                      height={88}
                    />
                    <Text className={styles.failureContentHeading}>
                      Policy expired!<br />Looks like it ran out of fuel.
                    </Text>
                    <Text className={styles.failureContentSubHead}>
                      Renew it to keep things running smoothly
                    </Text>
                  </div>
                  <Text className={styles.failureTroubleEmail}>If you’re still facing some trouble, write to us at <br /><span>cardrop@ackodrive.com</span></Text>
                </div>
              )
            }

            <div className={styles.failureModalInner}>
              <Text className={styles.failureModalInnerText}>
                You can still explore offers on brand-new cars.
              </Text>
              <Button
                className={styles.contestButton}
                onClick={handleFailureContest}
              >
                Explore ACKO Drive
              </Button>
            </div>
          </div>
        )
      }

    </div>
  );
};

export default ContestSection;